/**
 * Brainstorm Mode WebSocket Service
 *
 * Provides real-time WebSocket connections for Brainstorm (Design) mode
 * to enable multi-window synchronization and persistent streaming notifications.
 *
 * Events handled:
 * - brainstorm_user_message_added: When a user message is saved
 * - brainstorm_streaming_started: When AI streaming begins
 * - brainstorm_streaming_completed: When AI streaming completes
 * - brainstorm_streaming_cancelled: When user cancels streaming
 */

import { getApiBaseUrl as getHttpApiBaseUrl } from '../utils/axios';
import { getFreshToken } from '../utils/tokenUtils';

// Determine API base URL for WebSocket connections
const getWsBaseUrl = () => {
  const httpApiBase = getHttpApiBaseUrl();
  const httpOrigin = httpApiBase.replace(/\/?api$/, '');
  const wsOrigin = httpOrigin.replace(/^http/, 'ws');

  console.log('🔍 Brainstorm WebSocket URL construction:', {
    httpApiBase,
    httpOrigin,
    wsOrigin,
  });

  return wsOrigin;
};

// Brainstorm-specific WebSocket message types
export type BrainstormWebSocketEventType =
  | 'brainstorm_user_message_added'
  | 'brainstorm_streaming_started'
  | 'brainstorm_streaming_completed'
  | 'brainstorm_streaming_cancelled'
  | 'brainstorm_streaming_status'
  | 'connection_established'
  | 'streaming_status_response'
  | 'conversation_state_response'
  | 'pong'
  | 'error';

export interface BrainstormWebSocketMessage {
  type: BrainstormWebSocketEventType;
  data: {
    message_id?: string;
    thread_id?: string;
    project_id?: string;
    content?: string;
    streaming_state_id?: string;
    assistant_message_id?: string;
    status?: string;
    is_streaming?: boolean;
    timestamp?: string;
    message?: string;
    user_info?: {
      id: string;
      email?: string;
      username?: string;
      firstName?: string;
      lastName?: string;
    };
  };
  timestamp: string;
  original_ping_timestamp?: string;
}

export interface BrainstormWebSocketCallbacks {
  onMessage?: (message: BrainstormWebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  onReconnect?: () => void;
  onReconnectAttempt?: (attempt: number) => void;
}

export class BrainstormWebSocket {
  private ws: WebSocket | null = null;
  private threadId: string;
  private token: string;
  private callbacks: BrainstormWebSocketCallbacks;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = Infinity;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 60000;
  private isConnecting = false;
  private shouldReconnect = true;
  private reconnectResetInterval: ReturnType<typeof setInterval> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private networkStatusListener: (() => void) | null = null;
  private pongTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastPong = Date.now();
  private hasConnectedBefore = false;
  private visibilityChangeListener: (() => void) | null = null;
  private isPageVisible = true;
  private disconnectionReason = '';
  private consecutivePingFailures = 0;
  private maxPingFailures = 3;
  private consecutiveQuickFailures = 0;
  private lastConnectTime = 0;

  constructor(threadId: string, token: string, callbacks: BrainstormWebSocketCallbacks = {}) {
    this.threadId = threadId;
    this.token = token;
    this.callbacks = callbacks;
  }

  updateCallbacks(callbacks: BrainstormWebSocketCallbacks) {
    this.callbacks = callbacks;
  }

  updateToken(token: string): void {
    this.token = token;
  }

  async connect(): Promise<void> {
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isConnecting = true;

    try {
      const baseUrl = getWsBaseUrl();
      const wsUrl = `${baseUrl}/api/chat/ws/${this.threadId}?token=${this.token}`;

      console.log('🔌 [Brainstorm] Attempting WebSocket connection:', {
        baseUrl,
        wsUrl,
        threadId: this.threadId,
      });

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('🔌 [Brainstorm] WebSocket connected for thread:', this.threadId);
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.consecutiveQuickFailures = 0;
        this.lastConnectTime = Date.now();
        this.stopReconnectResetInterval();
        this.startPingPong();
        this.setupNetworkStatusListener();
        this.setupVisibilityListener();

        if (this.hasConnectedBefore) {
          console.log('🔄 [Brainstorm] WebSocket reconnected for thread:', this.threadId);
          this.callbacks.onReconnect?.();
        } else {
          console.log('✅ [Brainstorm] WebSocket initial connection for thread:', this.threadId);
          this.hasConnectedBefore = true;
          this.callbacks.onConnect?.();
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const message: BrainstormWebSocketMessage = JSON.parse(event.data);
          console.log('📨 [Brainstorm] WebSocket message:', message.type, message);

          // Handle pong messages
          if (message.type === 'pong') {
            this.lastPong = Date.now();
            this.consecutivePingFailures = 0;
            console.log('🏓 [Brainstorm] Pong received');
            if (this.pongTimeout) {
              clearTimeout(this.pongTimeout);
              this.pongTimeout = null;
            }
            return;
          }

          // Handle connection established
          if (message.type === 'connection_established') {
            console.log('✅ [Brainstorm] Connection established for thread:', this.threadId);
            return;
          }

          // Pass messages to callback
          this.callbacks.onMessage?.(message);
        } catch (error) {
          console.error('[Brainstorm] Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        const reason = event.reason || this.disconnectionReason || 'Unknown';
        console.log('🔌 [Brainstorm] WebSocket disconnected:', {
          threadId: this.threadId,
          code: event.code,
          reason,
          wasClean: event.wasClean,
        });

        this.isConnecting = false;
        this.stopPingPong();
        this.disconnectionReason = '';
        this.consecutivePingFailures = 0;
        this.callbacks.onDisconnect?.();

        const timeSinceConnect = Date.now() - this.lastConnectTime;
        if (this.lastConnectTime > 0 && timeSinceConnect < 3000) {
          this.consecutiveQuickFailures++;
        } else {
          this.consecutiveQuickFailures = 0;
        }

        if (this.consecutiveQuickFailures >= 5) {
          console.error('❌ [Brainstorm] Too many rapid connection failures — likely auth issue. Pausing reconnection.');
          this.consecutiveQuickFailures = 0;
          return;
        }

        // Don't reconnect for authentication errors (4001-4003) or normal closure (1000)
        const isAuthError = event.code >= 4001 && event.code <= 4003;
        const isNormalClosure = event.code === 1000 && reason !== 'Ping/pong failure';
        
        if (!isAuthError && !isNormalClosure && this.shouldReconnect) {
          this.scheduleReconnect();
        } else if (isAuthError) {
          console.error('❌ [Brainstorm] Authentication error, stopping reconnection attempts:', reason);
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ [Brainstorm] WebSocket error:', error);
        this.isConnecting = false;
        this.callbacks.onError?.(error);

        if (this.shouldReconnect) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('[Brainstorm] Error creating WebSocket:', error);
      this.isConnecting = false;
      this.callbacks.onError?.(error as Event);

      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, Math.min(this.reconnectAttempts - 1, 6)),
      this.maxReconnectDelay
    );

    console.log(`🔄 [Brainstorm] Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);

    this.callbacks.onReconnectAttempt?.(this.reconnectAttempts);
    this.startReconnectResetInterval();

    setTimeout(async () => {
      if (!this.shouldReconnect) return;

      console.log(`🔄 [Brainstorm] Attempting reconnection ${this.reconnectAttempts}`);

      try {
        const freshToken = await getFreshToken();
        if (freshToken) {
          this.token = freshToken;
        }
      } catch {
        const storedToken = localStorage.getItem('token') || '';
        if (storedToken) this.token = storedToken;
      }

      this.connect();
    }, delay);
  }

  private startReconnectResetInterval(): void {
    if (this.reconnectResetInterval) return;

    this.reconnectResetInterval = setInterval(() => {
      if (this.reconnectAttempts > 0) {
        console.log(`🔄 [Brainstorm] Resetting reconnection attempts`);
        this.reconnectAttempts = Math.max(0, this.reconnectAttempts - 5);
      }
    }, 10 * 60 * 1000);
  }

  private stopReconnectResetInterval(): void {
    if (this.reconnectResetInterval) {
      clearInterval(this.reconnectResetInterval);
      this.reconnectResetInterval = null;
    }
  }

  private setupNetworkStatusListener(): void {
    if (this.networkStatusListener || typeof navigator === 'undefined') return;

    this.networkStatusListener = () => {
      console.log('🌐 [Brainstorm] Network restored, attempting reconnection');
      if (this.shouldReconnect && (!this.ws || this.ws.readyState !== WebSocket.OPEN)) {
        this.reconnectAttempts = 0;
        this.consecutivePingFailures = 0;
        this.connect();
      }
    };

    window.addEventListener('online', this.networkStatusListener);
  }

  private removeNetworkStatusListener(): void {
    if (this.networkStatusListener) {
      window.removeEventListener('online', this.networkStatusListener);
      this.networkStatusListener = null;
    }
  }

  private setupVisibilityListener(): void {
    if (this.visibilityChangeListener || typeof document === 'undefined') return;

    this.visibilityChangeListener = () => {
      this.isPageVisible = !document.hidden;
      console.log(`👁️ [Brainstorm] Page visibility: ${this.isPageVisible ? 'visible' : 'hidden'}`);

      if (this.isPageVisible) {
        this.consecutivePingFailures = 0;
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeListener);
  }

  private removeVisibilityListener(): void {
    if (this.visibilityChangeListener) {
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
      this.visibilityChangeListener = null;
    }
  }

  private startPingPong(): void {
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        if (!this.isPageVisible) {
          console.log('⏸️ [Brainstorm] Skipping ping while page hidden');
          return;
        }
        this.sendPing();
      }
    }, 30000);
  }

  private stopPingPong(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private sendPing(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        const pingMessage = {
          type: 'ping',
          timestamp: new Date().toISOString(),
        };
        this.ws.send(JSON.stringify(pingMessage));
        console.log('🏓 [Brainstorm] Ping sent');

        this.pongTimeout = setTimeout(() => {
          this.consecutivePingFailures++;
          console.warn(`⚠️ [Brainstorm] No pong (${this.consecutivePingFailures}/${this.maxPingFailures})`);

          if (this.consecutivePingFailures >= this.maxPingFailures) {
            console.error(`❌ [Brainstorm] Too many ping failures, closing connection`);
            this.disconnectionReason = 'Ping/pong failure';
            this.ws?.close(1000, 'Ping/pong failure');
          }
        }, 10000);
      } catch (error) {
        console.error('❌ [Brainstorm] Error sending ping:', error);
        this.consecutivePingFailures++;
      }
    }
  }

  sendMessage(message: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('[Brainstorm] WebSocket not connected, cannot send message');
    }
  }

  requestStreamingStatus(): void {
    this.sendMessage({ type: 'get_streaming_status' });
  }

  requestConversationState(): void {
    this.sendMessage({ type: 'request_conversation_state' });
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.stopPingPong();
    this.stopReconnectResetInterval();
    this.removeNetworkStatusListener();
    this.removeVisibilityListener();
    this.disconnectionReason = 'User disconnected';

    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  isOpenOrConnecting(): boolean {
    if (!this.ws) return false;
    return this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING;
  }

  getConnectionState(): 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED' | 'UNKNOWN' {
    if (!this.ws) return 'CLOSED';

    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'OPEN';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }
}

// WebSocket manager for Brainstorm connections
export class BrainstormWebSocketManager {
  private connections: Map<string, BrainstormWebSocket> = new Map();

  connect(threadId: string, token: string, callbacks: BrainstormWebSocketCallbacks = {}): BrainstormWebSocket {
    const existing = this.connections.get(threadId);
    if (existing && existing.isOpenOrConnecting()) {
      existing.updateCallbacks(callbacks);
      return existing;
    }

    if (existing) {
      existing.disconnect();
      this.connections.delete(threadId);
    }

    const ws = new BrainstormWebSocket(threadId, token, callbacks);
    this.connections.set(threadId, ws);

    ws.connect().catch((error) => {
      console.error('[Brainstorm] Failed to connect WebSocket:', threadId, error);
    });

    return ws;
  }

  disconnect(threadId: string): void {
    const ws = this.connections.get(threadId);
    if (ws) {
      ws.disconnect();
      this.connections.delete(threadId);
    }
  }

  getConnection(threadId: string): BrainstormWebSocket | undefined {
    return this.connections.get(threadId);
  }

  disconnectAll(): void {
    this.connections.forEach((ws) => {
      ws.disconnect();
    });
    this.connections.clear();
  }

  getConnectionCount(): number {
    return this.connections.size;
  }

  getConnectionStates(): Record<string, string> {
    const states: Record<string, string> = {};
    this.connections.forEach((ws, threadId) => {
      states[threadId] = ws.getConnectionState();
    });
    return states;
  }
}

// Global Brainstorm WebSocket manager instance
export const brainstormWebsocketManager = new BrainstormWebSocketManager();
